import { useRouter } from "next/router";
import { useQuery } from "@tanstack/react-query";
import ListPageLayout from "components/Layout/ListPageLayout";
import NewEventCard from "components/NewEventCard";
import Skeleton from "components/Skeleton/Skeleton";
import { useLang } from "hooks/useLang";
import filterByCategoryTitle from "api/sections/filterByCategoryTitle";
import { sortEventsByStart } from "utils/helpers/events/sortEvents";
import dynamic from "next/dynamic";
import { NextSeo } from "next-seo";

const MainLayout = dynamic(() => import("components/Layout/MainLayout"), {
  ssr: false,
});

export async function getServerSideProps(context) {
  const { title } = context.params;

  const res = await filterByCategoryTitle(title);

  return {
    props: {
      data: res,
      error: null,
    },
  };
}

const CategoriesPage = (props) => {
  const router = useRouter();
  const { t } = useLang();

  const title = router.query?.title as string;

  const { data, isLoading } = useQuery({
    queryKey: ["filtered-events-by-categories", title],
    queryFn: () => filterByCategoryTitle(title),
    enabled: !!title,
    initialData: props.data?.payload,
  });

  const events = data?.payload;
  const sortedEvents = sortEventsByStart(data?.payload);

  const baseUrl = (process.env.NEXT_PUBLIC_SITE_URL || "https://pass-tickets.com").replace(/\/$/, "");
  const currentPath = (router.asPath || "/").split("#")[0].split("?")[0] || "/";
  const canonicalUrl = `${baseUrl}${currentPath}`;

  return (
    <MainLayout>
      <NextSeo
        title={t(title)}
        canonical={canonicalUrl}
        languageAlternates={(() => {
          const pathNoLocale = currentPath.replace(/^\/(en-US|ar-SA)(?=\/|$)/, "");
          const defaultHref = `${baseUrl}${pathNoLocale || "/"}`;
          const enHref = `${baseUrl}/en-US${pathNoLocale || "/"}`;
          return [
            { hrefLang: "x-default", href: defaultHref },
            { hrefLang: "ar", href: defaultHref },
            { hrefLang: "en", href: enHref },
          ];
        })()}
        openGraph={{ url: canonicalUrl }}
      />
      <ListPageLayout title={t(title)} desc={t("category_title")}>
        {isLoading ? (
          <Skeleton
            className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 pt-20"
            type={"event_card"}
          />
        ) : events?.length === 0 ? (
          <Skeleton
            className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 pt-20"
            type="event_card_empty"
          />
        ) : (
          <div className="grid grid-cols-1 gap-5 pt-20 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"></div>
        )}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {sortedEvents.map((item) => (
            <NewEventCard event={item} key={item._id} priorityImage={true} />
          ))}
        </div>
      </ListPageLayout>
    </MainLayout>
  );
};

export default CategoriesPage;
